import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { GBP } from "@/lib/helpers";
import { pretty } from "@/lib/helpers";
import { useFeatures } from "@/lib/useFeatures";
import { FeatureLocked } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Minus, Trash2, ShoppingCart, AlertTriangle, CalendarClock, CheckCircle2, XCircle, UtensilsCrossed } from "lucide-react";
import { fileUrl } from "@/lib/upload";
import { toast } from "sonner";

const TYPES = [
  { key: "DINE_IN", label: "Dine-in" },
  { key: "COLLECTION", label: "Collection" },
  { key: "DELIVERY", label: "Delivery" },
  { key: "BOOKING", label: "Booking" },
];

function sig(item, opts) {
  return item.id + "|" + opts.map((o) => o.value_id).sort().join(",");
}

function OptionsDialog({ item, basePrice, onClose, onConfirm }) {
  const [sel, setSel] = useState({});
  if (!item) return null;
  const base = basePrice ?? item.price;
  const toggle = (group, value) => {
    setSel((p) => {
      const cur = p[group.id] || [];
      if (group.type === "MULTI_SELECT") {
        const has = cur.find((v) => v.id === value.id);
        return { ...p, [group.id]: has ? cur.filter((v) => v.id !== value.id) : [...cur, value] };
      }
      return { ...p, [group.id]: [value] };
    });
  };
  const chosen = Object.values(sel).flat();
  const modifier = chosen.reduce((s, v) => s + (v.price_modifier || 0), 0);
  const missingRequired = (item.options || []).some((g) => g.required && !(sel[g.id] || []).length);
  const confirm = () => {
    const opts = chosen.map((v) => {
      const group = item.options.find((g) => (g.values || []).some((x) => x.id === v.id));
      return { name: group?.name, value: v.name, value_id: v.id, price_modifier: v.price_modifier || 0 };
    });
    onConfirm(item, opts, base + modifier);
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">{item.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto">
          {(item.options || []).map((g) => (
            <div key={g.id}>
              <p className="text-sm font-medium mb-2">{g.name} {g.required && <span className="text-red-500">*</span>}
                <span className="text-muted-foreground text-xs ml-1">{g.type === "MULTI_SELECT" ? "(choose any)" : "(choose one)"}</span></p>
              <div className="flex flex-wrap gap-2">
                {(g.values || []).filter((v) => v.is_available !== false).map((v) => {
                  const active = (sel[g.id] || []).find((x) => x.id === v.id);
                  return (
                    <button key={v.id} data-testid={`pos-opt-${v.id}`} onClick={() => toggle(g, v)}
                      className={`h-10 px-3 rounded-lg text-sm border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
                      {v.name}{v.price_modifier ? ` +${GBP(v.price_modifier)}` : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button disabled={missingRequired} onClick={confirm} data-testid="pos-opt-confirm" className="h-11 w-full">
            Add · {GBP(base + modifier)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function POS() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [menu, setMenu] = useState([]);
  const [cats, setCats] = useState([]);
  const [tables, setTables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [activeCat, setActiveCat] = useState("ALL");
  const [activeSub, setActiveSub] = useState("ALL");
  const [cartOpen, setCartOpen] = useState(false);
  const [type, setType] = useState("COLLECTION");
  const [tableIds, setTableIds] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [deliveryCheck, setDeliveryCheck] = useState(null);
  const [lines, setLines] = useState([]);
  const [optItem, setOptItem] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editRef, setEditRef] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [foundOrders, setFoundOrders] = useState([]);
  const [stripeCfg, setStripeCfg] = useState({ enabled: false, has_key: false });
  const [allergens, setAllergens] = useState([]);
  const [activeAllergens, setActiveAllergens] = useState([]);
  const [bands, setBands] = useState([]);

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}`).then((r) => setRestaurant(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/menu/items`).then((r) => setMenu(r.data.filter((m) => m.is_available))).catch(() => {});
    api.get(`/restaurants/${rid}/menu/categories`).then((r) => setCats(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/tables`).then((r) => setTables(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/bookings`).then((r) => setBookings(r.data.filter((b) => ["PENDING", "CONFIRMED"].includes(b.status)))).catch(() => {});
    api.get(`/restaurants/${rid}/stripe-config`).then((r) => setStripeCfg(r.data)).catch(() => {});
    api.get(`/allergens`).then((r) => setAllergens(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/price-bands`).then((r) => setBands(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  const posContext = (t) => (t === "DINE_IN" || t === "BOOKING") ? "DINE_IN" : (t === "DELIVERY" ? "DELIVERY" : "COLLECTION");
  const activeBand = useMemo(() => {
    const ctx = posContext(type);
    return bands.find((b) => (b.contexts || []).includes(ctx)) || bands.find((b) => b.is_default) || null;
  }, [bands, type]);
  const priceOf = useCallback((item) => {
    const bp = activeBand ? (item.band_prices || {})[activeBand.id] : undefined;
    return (bp !== undefined && bp !== null && bp !== "") ? Number(bp) : item.price;
  }, [activeBand]);

  const allergenMap = useMemo(() => Object.fromEntries(allergens.map((a) => [a.id, a.name])), [allergens]);
  const itemAllergens = useCallback(
    (item) => (item.allergen_links || []).map((l) => allergenMap[l.allergen_id]).filter(Boolean),
    [allergenMap]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sid = p.get("session_id");
    if (p.get("order_pay_cancelled")) { toast.info("Payment cancelled — order saved as unpaid"); window.history.replaceState({}, "", `/r/${rid}/pos`); return; }
    if (!sid || !p.get("order_paid")) return;
    (async () => {
      try {
        const { data } = await api.get(`/order-payments/status/${sid}`);
        if (data.payment_status === "paid") toast.success("Payment received — order marked paid");
        else toast.info("Payment still processing");
      } catch { toast.error("Could not confirm payment"); }
      window.history.replaceState({}, "", `/r/${rid}/pos`);
    })();
  }, [rid]);

  const catName = useCallback((id) => cats.find((c) => c.id === id)?.name || "Other", [cats]);

  const addLine = (item, opts, unitPrice) => {
    const s = sig(item, opts);
    const allergs = itemAllergens(item);
    setLines((p) => {
      const ex = p.find((l) => l.sig === s);
      if (ex) return p.map((l) => l.sig === s ? { ...l, quantity: l.quantity + 1 } : l);
      return [...p, { sig: s, menu_item_id: item.id, name: item.name, category_id: item.category_id, unit_price: unitPrice, options: opts, quantity: 1, allergens: allergs }];
    });
    setOptItem(null);
  };
  const tapItem = (item) => {
    if ((item.options || []).length > 0) setOptItem(item);
    else addLine(item, [], priceOf(item));
  };
  const setQty = (s, d) => setLines((p) => p.map((l) => l.sig === s ? { ...l, quantity: Math.max(1, l.quantity + d) } : l).filter((l) => l.quantity > 0));
  const remove = (s) => setLines((p) => p.filter((l) => l.sig !== s));
  const setNote = (s, note) => setLines((p) => p.map((l) => l.sig === s ? { ...l, notes: note } : l));

  // Re-price the basket when the order type (channel) changes so band prices apply.
  useEffect(() => {
    if (editOrderId) return;
    setLines((prev) => prev.map((l) => {
      const mi = menu.find((m) => m.id === l.menu_item_id);
      if (!mi) return l;
      const optTotal = (l.options || []).reduce((s, o) => s + (o.price_modifier || 0), 0);
      return { ...l, unit_price: priceOf(mi) + optTotal };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBand]);

  const resetCart = () => {
    setLines([]); setName(""); setPhone(""); setAddress(""); setPostcode(""); setTableIds([]);
    setBookingId(null); setDeliveryCheck(null); setEditOrderId(null); setEditRef(""); setFoundOrders([]); setOrderSearch(""); setCartOpen(false); setActiveAllergens([]);
  };

  const toggleAllergen = (name) =>
    setActiveAllergens((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  const lineFlagged = (l) => activeAllergens.some((a) => (l.allergens || []).includes(a));

  const searchOrders = async () => {
    if (!orderSearch.trim()) return;
    try {
      const { data } = await api.get(`/restaurants/${rid}/orders`, { params: { search: orderSearch.trim() } });
      const editable = data.filter((o) => !["COMPLETED", "CANCELLED", "REJECTED"].includes(o.status));
      setFoundOrders(editable);
      if (editable.length === 0) toast.info("No open orders found");
    } catch { toast.error("Search failed"); }
  };

  const loadOrder = (o) => {
    const newLines = (o.items || []).map((it) => {
      const opts = (it.options_snapshot || []).map((op) => ({ name: op.name, value: op.value, value_id: op.value_id, price_modifier: op.price_modifier || 0 }));
      return { sig: it.menu_item_id + "|" + opts.map((x) => x.value_id).sort().join(",") + "|" + it.id,
        menu_item_id: it.menu_item_id, name: it.item_name_snapshot, category_id: null,
        unit_price: it.unit_price, options: opts, quantity: it.quantity, notes: it.notes || "",
        allergens: (it.allergens_snapshot || []).map((a) => a.name || a).filter(Boolean) };
    });
    setLines(newLines);
    setName(o.customer_name || ""); setPhone(o.customer_phone || "");
    setTableIds(o.table_ids || []); setEditOrderId(o.id); setEditRef(o.order_number);
    setType(o.order_type === "DINE_IN" ? "DINE_IN" : (o.fulfillment_type === "DELIVERY" ? "DELIVERY" : "COLLECTION"));
    setFoundOrders([]); setOrderSearch("");
    toast.success(`Editing ${o.order_number}`);
  };

  const subtotal = lines.reduce((s, l) => s + l.unit_price * l.quantity, 0);
  const deliveryFee = type === "DELIVERY" && deliveryCheck?.in_zone ? (deliveryCheck.fee || 0) : 0;
  const total = subtotal + deliveryFee;

  const filtered = (activeCat === "ALL" ? menu : menu.filter((m) => m.category_id === activeCat))
    .filter((m) => activeSub === "ALL" || (m.subcategory || "") === activeSub);
  // distinct subcategories within the active category (for the secondary filter)
  const subcats = useMemo(() => {
    if (activeCat === "ALL") return [];
    const out = [];
    menu.filter((m) => m.category_id === activeCat).forEach((m) => {
      const s = m.subcategory || "";
      if (s && !out.includes(s)) out.push(s);
    });
    return out;
  }, [menu, activeCat]);
  const pickCat = (id) => { setActiveCat(id); setActiveSub("ALL"); };
  // group filtered items by subcategory
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((m) => { const k = m.subcategory || ""; (g[k] = g[k] || []).push(m); });
    return g;
  }, [filtered]);

  // cart grouped by category
  const cartByCat = useMemo(() => {
    const g = {};
    lines.forEach((l) => { const k = catName(l.category_id); (g[k] = g[k] || []).push(l); });
    return g;
  }, [lines, catName]);

  // distinct allergens present across everything currently in the cart
  const cartAllergens = useMemo(() => {
    const set = [];
    lines.forEach((l) => (l.allergens || []).forEach((a) => { if (!set.includes(a)) set.push(a); }));
    return set.sort();
  }, [lines]);

  const checkDelivery = async () => {
    if (!postcode.trim()) return toast.error("Enter a postcode");
    try {
      const { data } = await api.post(`/restaurants/${rid}/delivery/check`, { postcode });
      setDeliveryCheck(data);
      if (!data.configured) toast.error("Delivery area not configured in Settings");
    } catch { toast.error("Could not check postcode"); }
  };

  const pickBooking = (b) => {
    setBookingId(b.id);
    setName(b.customer_name || "");
    setPhone(b.customer_phone || "");
    setTableIds(b.table_ids || []);
    setType("BOOKING");
    toast.success(`Loaded booking ${b.booking_ref || ""}`);
  };

  const place = async (payNow = false) => {
    if (lines.length === 0) return toast.error("Add at least one item");
    const isDineIn = type === "DINE_IN" || type === "BOOKING";
    if (isDineIn && tableIds.length === 0) { toast.dismiss(); return toast.error("Select at least one table"); }
    if (type === "DELIVERY") {
      if (!address.trim() || !postcode.trim()) { toast.dismiss(); return toast.error("Delivery needs address + postcode"); }
      if (deliveryCheck && deliveryCheck.in_zone === false) { toast.dismiss(); return toast.error("Address is outside the delivery area"); }
    }
    const payload = {
      order_type: isDineIn ? "DINE_IN" : "TAKEAWAY",
      source: "STAFF_DASHBOARD",
      customer_name: name || (isDineIn ? "Table" : "Guest"),
      customer_phone: phone || null,
      table_id: isDineIn ? (tableIds[0] || null) : null,
      table_ids: isDineIn ? tableIds : [],
      booking_id: type === "BOOKING" ? bookingId : null,
      fulfillment_type: isDineIn ? "COLLECTION" : type,
      delivery_address: type === "DELIVERY" ? address : null,
      delivery_postcode: type === "DELIVERY" ? postcode : null,
      delivery_fee: deliveryFee,
      declared_allergens: activeAllergens,
      items: lines.map((l) => ({ menu_item_id: l.menu_item_id, quantity: l.quantity, options: l.options, notes: l.notes || null })),
    };
    setPlacing(true);
    try {
      if (editOrderId) {
        const { data } = await api.put(`/orders/${editOrderId}`, {
          items: payload.items, customer_name: payload.customer_name, customer_phone: payload.customer_phone,
          table_ids: payload.table_ids, table_id: payload.table_id, delivery_fee: deliveryFee,
        });
        toast.success(`Order ${data.order_number} updated`);
      } else {
        const { data } = await api.post(`/restaurants/${rid}/orders`, payload);
        if (payNow) {
          try {
            const { data: pay } = await api.post(`/restaurants/${rid}/orders/${data.id}/pay`, { origin_url: window.location.origin });
            window.location.href = pay.url;
            return;
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Order saved but payment could not start");
          }
        } else {
          toast.success(`Order ${data.order_number} sent to kitchen`);
        }
      }
      resetCart();
      load();
    } catch { toast.error("Could not save order"); }
    finally { setPlacing(false); }
  };

  const toggleTable = (id) => setTableIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const features = useFeatures(rid);
  if (features && !features.POS) return <div className="min-h-screen bg-background"><FeatureLocked label="POS Terminal" /></div>;

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <header className="shrink-0 flex items-center justify-between border-b border-border bg-card px-4 h-14">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" data-testid="pos-back" onClick={() => navigate(`/r/${rid}/dashboard`)}><ArrowLeft /></Button>
          <span className="font-display font-bold">POS · {restaurant?.name}</span>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Menu */}
        <div className="flex-1 min-w-0 flex flex-col p-4">
          <div className="flex gap-2 overflow-x-auto pb-3 no-scrollbar">
            <button data-testid="pos-cat-ALL" onClick={() => pickCat("ALL")}
              className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium ${activeCat === "ALL" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>All</button>
            {cats.map((c) => (
              <button key={c.id} data-testid={`pos-cat-${c.id}`} onClick={() => pickCat(c.id)}
                className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium ${activeCat === c.id ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{c.name}</button>
            ))}
          </div>
          {subcats.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-3 no-scrollbar" data-testid="pos-subcat-row">
              <button data-testid="pos-subcat-ALL" onClick={() => setActiveSub("ALL")}
                className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-medium border transition-colors ${activeSub === "ALL" ? "bg-primary/10 text-primary border-primary/30" : "bg-muted text-muted-foreground border-transparent hover:border-border"}`}>All</button>
              {subcats.map((s) => (
                <button key={s} data-testid={`pos-subcat-${s}`} onClick={() => setActiveSub(s)}
                  className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-medium border transition-colors ${activeSub === s ? "bg-primary/10 text-primary border-primary/30" : "bg-muted text-muted-foreground border-transparent hover:border-border"}`}>{s}</button>
              ))}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto pb-24 lg:pb-0">
            {Object.entries(grouped).map(([sub, items]) => (
              <div key={sub || "none"} className="mb-4">
                {sub && activeSub === "ALL" && <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{sub}</p>}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {items.map((m) => (
                    <button key={m.id} data-testid={`pos-item-${m.id}`} onClick={() => tapItem(m)}
                      className="group relative rounded-2xl border border-border bg-card overflow-hidden text-left hover:border-primary/50 hover:shadow-md active:scale-[0.98] transition-all flex flex-col">
                      <div className="relative h-32 sm:h-36 bg-secondary shrink-0">
                        {m.image_url
                          ? <img src={fileUrl(m.image_url)} alt={m.name} className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-primary/25"><UtensilsCrossed className="h-9 w-9" /></div>}
                        {m.is_popular && <span className="absolute top-2 left-2 text-[10px] font-semibold rounded-full bg-amber-500 text-white px-2 py-0.5">Popular</span>}
                        <span className="absolute bottom-2 right-2 h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg group-active:scale-90 transition-transform"><Plus className="h-5 w-5" /></span>
                      </div>
                      <div className="p-3">
                        <p className="font-medium leading-tight text-sm sm:text-[15px] line-clamp-2 min-h-[2.5em]">{m.name}
                          {(m.options || []).length > 0 && <span className="text-[10px] ml-1 text-primary">· options</span>}</p>
                        <p className="font-display font-bold text-lg mt-1">{GBP(priceOf(m))}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-muted-foreground py-8 text-center">No items.</p>}
          </div>
        </div>

        {/* Cart */}
        <aside className={`bg-card flex flex-col min-h-0 border-border
          fixed inset-0 z-40 ${cartOpen ? "flex" : "hidden"}
          lg:static lg:z-auto lg:flex lg:w-full lg:max-w-sm lg:shrink-0 lg:border-l`}>
          <div className="lg:hidden flex items-center justify-between px-4 h-14 border-b border-border shrink-0">
            <span className="font-display font-bold">Your order</span>
            <button onClick={() => setCartOpen(false)} data-testid="pos-cart-close" className="text-primary font-medium text-sm">Back to menu</button>
          </div>
          <div className="p-4 border-b border-border shrink-0">
            {editOrderId && (
              <div className="mb-3 flex items-center justify-between rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm" data-testid="pos-edit-banner">
                <span className="text-amber-800 font-medium">Editing {editRef}</span>
                <button onClick={resetCart} data-testid="pos-cancel-edit" className="text-amber-700 underline text-xs">Cancel</button>
              </div>
            )}
            <div className="mb-3">
              <div className="flex gap-2">
                <Input data-testid="pos-order-search" placeholder="Edit order — name / phone / ref" value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchOrders()} className="h-10" />
                <Button variant="outline" className="h-10" data-testid="pos-order-search-btn" onClick={searchOrders}>Find</Button>
              </div>
              {foundOrders.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5" data-testid="pos-found-orders">
                  {foundOrders.map((o) => (
                    <button key={o.id} data-testid={`pos-found-${o.id}`} onClick={() => loadOrder(o)}
                      className="w-full text-left rounded-lg border border-border p-2 text-sm hover:border-primary/50">
                      <span className="font-medium">{o.order_number}</span> · {o.customer_name || "Guest"}
                      <span className="block text-xs text-muted-foreground">{pretty(o.status)} · {GBP(o.total)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {TYPES.map((t) => (
                <button key={t.key} data-testid={`pos-type-${t.key}`} onClick={() => { setType(t.key); if (t.key !== "BOOKING") setBookingId(null); }}
                  className={`h-11 rounded-lg text-xs font-semibold ${type === t.key ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{t.label}</button>
              ))}
            </div>

            {type === "BOOKING" && (
              <div className="mt-3 max-h-40 overflow-y-auto space-y-1.5" data-testid="pos-booking-list">
                {bookings.length === 0 && <p className="text-xs text-muted-foreground">No open bookings.</p>}
                {bookings.map((b) => (
                  <button key={b.id} data-testid={`pos-booking-${b.id}`} onClick={() => pickBooking(b)}
                    className={`w-full text-left rounded-lg border p-2.5 text-sm ${bookingId === b.id ? "border-primary bg-primary/5" : "border-border"}`}>
                    <span className="font-medium">{b.customer_name}</span> · {b.guest_count} guests
                    <span className="block text-xs text-muted-foreground">{b.booking_date} {b.booking_time} · {(b.table_ids || []).length} table(s)</span>
                  </button>
                ))}
              </div>
            )}

            {(type === "DINE_IN" || type === "BOOKING") && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1.5">Tables (tap to select)</p>
                <div className="flex flex-wrap gap-1.5" data-testid="pos-tables">
                  {tables.map((t) => (
                    <button key={t.id} data-testid={`pos-table-${t.id}`} onClick={() => toggleTable(t.id)}
                      className={`h-9 px-3 rounded-lg text-xs border ${tableIds.includes(t.id) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
                      {t.table_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {type === "DELIVERY" && (
              <div className="mt-3 space-y-2">
                <Input data-testid="pos-address" placeholder="Delivery address" value={address} onChange={(e) => setAddress(e.target.value)} className="h-11" />
                <div className="flex gap-2">
                  <Input data-testid="pos-postcode" placeholder="Postcode" value={postcode} onChange={(e) => { setPostcode(e.target.value); setDeliveryCheck(null); }} className="h-11" />
                  <Button variant="outline" className="h-11" data-testid="pos-check-delivery" onClick={checkDelivery}>Check</Button>
                </div>
                {deliveryCheck && deliveryCheck.configured && deliveryCheck.in_zone !== null && (
                  <p data-testid="pos-delivery-result" className={`text-xs flex items-center gap-1 ${deliveryCheck.in_zone ? "text-emerald-600" : "text-red-600"}`}>
                    {deliveryCheck.in_zone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {deliveryCheck.message}
                  </p>
                )}
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <Input data-testid="pos-name" placeholder="Customer name" value={name} onChange={(e) => setName(e.target.value)} className="h-11" />
              <Input data-testid="pos-phone" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            {lines.length === 0 && (
              <div className="text-center text-muted-foreground py-12"><ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-40" /> Tap items to add</div>
            )}
            {cartAllergens.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="pos-allergen-checklist">
                <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="h-3.5 w-3.5" /> Allergen check — tap to highlight items
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {cartAllergens.map((a) => {
                    const on = activeAllergens.includes(a);
                    return (
                      <button key={a} data-testid={`pos-allergen-${a}`} onClick={() => toggleAllergen(a)}
                        className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${on ? "bg-red-600 text-white border-red-600" : "bg-white text-amber-900 border-amber-300 hover:border-red-400"}`}>
                        {a}
                      </button>
                    );
                  })}
                </div>
                {activeAllergens.length > 0 && (
                  <button data-testid="pos-allergen-clear" onClick={() => setActiveAllergens([])}
                    className="mt-2 text-[11px] text-amber-700 underline">Clear highlights</button>
                )}
              </div>
            )}
            {Object.entries(cartByCat).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{cat}</p>
                <div className="space-y-2">
                  {items.map((l) => {
                    const flagged = lineFlagged(l);
                    return (
                    <div key={l.sig} data-testid={`pos-cart-${l.menu_item_id}`}
                      className={`rounded-lg border p-2.5 transition-all ${flagged ? "border-red-500 ring-2 ring-red-500/60 bg-red-50 shadow-[0_0_0_3px_rgba(239,68,68,0.15)]" : "border-border"}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{l.name}</p>
                          {l.options.length > 0 && <p className="text-[11px] text-muted-foreground truncate">{l.options.map((o) => o.value).join(", ")}</p>}
                          {flagged && (l.allergens || []).length > 0 && (
                            <p className="text-[11px] font-semibold text-red-600 flex items-center gap-1 truncate" data-testid={`pos-cart-allergen-flag-${l.menu_item_id}`}>
                              <AlertTriangle className="h-3 w-3 shrink-0" /> Contains: {(l.allergens || []).filter((a) => activeAllergens.includes(a)).join(", ")}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">{GBP(l.unit_price * l.quantity)}</p>
                        </div>
                        <Button size="icon" variant="outline" className="h-9 w-9" data-testid={`pos-minus-${l.menu_item_id}`} onClick={() => setQty(l.sig, -1)}><Minus className="h-4 w-4" /></Button>
                        <span className="w-6 text-center font-medium">{l.quantity}</span>
                        <Button size="icon" variant="outline" className="h-9 w-9" data-testid={`pos-plus-${l.menu_item_id}`} onClick={() => setQty(l.sig, 1)}><Plus className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => remove(l.sig)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                      <Input data-testid={`pos-note-${l.menu_item_id}`} value={l.notes || ""} onChange={(e) => setNote(l.sig, e.target.value)}
                        placeholder="Add note (e.g. no onions)" className="h-8 mt-2 text-xs" />
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-border shrink-0 bg-card">
            {deliveryFee > 0 && <div className="flex justify-between text-sm text-muted-foreground mb-1"><span>Delivery fee</span><span>{GBP(deliveryFee)}</span></div>}
            <div className="flex justify-between font-display text-xl font-bold mb-3">
              <span>Total</span><span data-testid="pos-total">{GBP(total)}</span>
            </div>
            {(() => {
              const canPay = stripeCfg.enabled && stripeCfg.has_key;
              const isDineIn = type === "DINE_IN" || type === "BOOKING";
              if (editOrderId) {
                return <Button data-testid="pos-place-order" onClick={() => place(false)} disabled={placing} className="w-full h-14 text-base">{placing ? "Saving…" : "Update order"}</Button>;
              }
              if (type === "DELIVERY") {
                return (<>
                  <Button data-testid="pos-pay-now" onClick={() => place(true)} disabled={placing || !canPay} className="w-full h-14 text-base">{placing ? "…" : "Pay now & place order"}</Button>
                  {!canPay && <p className="text-xs text-amber-600 mt-2">Delivery orders require payment. Enable online card payments in Settings → Payments.</p>}
                </>);
              }
              if (isDineIn) {
                return (<>
                  <Button data-testid="pos-place-order" onClick={() => place(false)} disabled={placing} className="w-full h-14 text-base">{placing ? "Sending…" : "Place order (pay later)"}</Button>
                  <p className="text-xs text-muted-foreground mt-2">Take payment later from the order card to close the table.</p>
                </>);
              }
              return (<>
                <Button data-testid="pos-place-order" onClick={() => place(false)} disabled={placing} className="w-full h-12 text-base mb-2">{placing ? "Sending…" : "Place order · pay on collection"}</Button>
                <Button data-testid="pos-pay-now" variant="outline" onClick={() => place(true)} disabled={placing || !canPay} className="w-full h-12 text-base">Pay now & place order</Button>
                {!canPay && <p className="text-xs text-muted-foreground mt-2">Enable online card payments in Settings → Payments to take payment now.</p>}
              </>);
            })()}
          </div>
        </aside>
      </div>

      {/* Mobile: floating view-order bar */}
      {!cartOpen && (
        <button onClick={() => setCartOpen(true)} data-testid="pos-mobile-cart"
          className="lg:hidden fixed bottom-4 inset-x-4 z-30 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-between px-5 shadow-xl active:scale-[0.98] transition-transform">
          <span className="flex items-center gap-2 font-medium"><ShoppingCart className="h-5 w-5" /> {lines.reduce((s, l) => s + l.quantity, 0)} item(s)</span>
          <span className="font-display font-bold">{GBP(total)}</span>
        </button>
      )}

      <OptionsDialog item={optItem} basePrice={optItem ? priceOf(optItem) : undefined} onClose={() => setOptItem(null)} onConfirm={addLine} />
    </div>
  );
}
